package org.teddapp.project.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties

/**
 * Champ de date Encre. Plus jamais de « AAAA-MM-JJ » à taper : le champ affiche la date en
 * français et s'ouvre sur un calendrier avec des raccourcis (aujourd'hui, dans 7 jours, fin du mois…).
 *
 * @param value date ISO ou chaîne vide pour « aucune ».
 * @param maxIso borne haute facultative (les jours après sont grisés).
 */
@Composable
fun TeddDateField(
    label: String?,
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    placeholder: String = "Aucune échéance",
    height: Dp = 44.dp,
    maxIso: String? = null,
    minIso: String? = null,
    clearable: Boolean = true,
) {
    var open by remember { mutableStateOf(false) }
    val shape = RoundedCornerShape(TeddTokens.RSm)
    val valid = TeddDates.isValidIso(value)
    Column(modifier = modifier) {
        if (label != null) {
            Text(
                text = label.uppercase(),
                style = TeddType.Overline,
                color = TeddTokens.InkMuted,
                modifier = Modifier.padding(bottom = 6.dp, start = 2.dp),
            )
        }
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(height)
                .clip(shape)
                .background(TeddTokens.SurfaceSunken, shape)
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = { open = true },
                )
                .padding(start = 12.dp, end = 6.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            TeddIcon(name = "calendar", size = 14.dp, color = if (valid) TeddTokens.LimeStrong else TeddTokens.InkMuted, strokeWidth = 1.8.dp)
            Text(
                text = if (valid) TeddDates.formatShort(value, withYear = true) ?: value else placeholder,
                color = if (valid) TeddTokens.Ink else TeddTokens.InkSubtle,
                fontSize = 14.sp,
                fontWeight = if (valid) FontWeight.Medium else FontWeight.Normal,
                maxLines = 1,
                modifier = Modifier.weight(1f),
            )
            if (valid) {
                TeddDates.formatRelative(value)?.takeIf { it != TeddDates.formatShort(value) }?.let { rel ->
                    Text(rel, color = TeddTokens.InkMuted, fontSize = 11.sp)
                }
            }
            if (valid && clearable) {
                TeddIconButton(onClick = { onValueChange("") }, size = 28.dp) {
                    Box(modifier = Modifier.rotate(45f)) {
                        TeddIcon(name = "plus", size = 13.dp, color = TeddTokens.InkMuted, strokeWidth = 2.dp)
                    }
                }
            }
        }
    }
    if (open) {
        Dialog(onDismissRequest = { open = false }, properties = DialogProperties(usePlatformDefaultWidth = false)) {
            TeddDatePickerCard(
                value = value.takeIf { valid },
                minIso = minIso,
                maxIso = maxIso,
                clearable = clearable,
                onPick = { picked -> onValueChange(picked ?: ""); open = false },
                onDismiss = { open = false },
            )
        }
    }
}

/** Calendrier mensuel avec raccourcis. Autonome : utilisable dans une feuille ou un dialogue. */
@Composable
fun TeddDatePickerCard(
    value: String?,
    onPick: (String?) -> Unit,
    onDismiss: () -> Unit,
    minIso: String? = null,
    maxIso: String? = null,
    clearable: Boolean = true,
) {
    val today = TeddDates.todayIso
    val anchor = value ?: today
    var year by remember(anchor) { mutableStateOf(anchor.substring(0, 4).toInt()) }
    var month by remember(anchor) { mutableStateOf(anchor.substring(5, 7).toInt()) }
    val minDays = TeddDates.daysFromIso(minIso)
    val maxDays = TeddDates.daysFromIso(maxIso)
    val allowed: (String) -> Boolean = { iso ->
        val d = TeddDates.daysFromIso(iso)
        d != null && (minDays == null || d >= minDays) && (maxDays == null || d <= maxDays)
    }

    Column(
        modifier = Modifier
            .width(320.dp)
            .teddSurface(shape = RoundedCornerShape(TeddTokens.RXl), elevation = 12.dp)
            .padding(18.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        // Raccourcis
        FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
            QuickDateChip("Aujourd'hui", today, value, allowed, onPick)
            QuickDateChip("Dans 7 jours", TeddDates.addDays(today, 7), value, allowed, onPick)
            QuickDateChip("Fin du mois", TeddDates.endOfMonth(today), value, allowed, onPick)
            QuickDateChip("Fin d'année", TeddDates.endOfYear(today), value, allowed, onPick)
            if (clearable && value != null) {
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(TeddTokens.RFull))
                        .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { onPick(null) }
                        .padding(horizontal = 10.dp, vertical = 5.dp),
                ) { Text("Aucune", color = TeddTokens.Danger, fontSize = 12.sp, fontWeight = FontWeight.SemiBold) }
            }
        }
        // En-tête mois
        Row(verticalAlignment = Alignment.CenterVertically) {
            TeddIconButton(onClick = { if (month == 1) { month = 12; year -= 1 } else month -= 1 }, size = 30.dp) {
                Box(modifier = Modifier.rotate(180f)) { TeddIcon(name = "chevron", size = 14.dp, color = TeddTokens.InkMuted) }
            }
            Text(
                text = TeddDates.monthTitle(year, month).replaceFirstChar { it.uppercase() },
                style = TeddType.Heading,
                color = TeddTokens.Ink,
                textAlign = TextAlign.Center,
                modifier = Modifier.weight(1f),
            )
            TeddIconButton(onClick = { if (month == 12) { month = 1; year += 1 } else month += 1 }, size = 30.dp) {
                TeddIcon(name = "chevron", size = 14.dp, color = TeddTokens.InkMuted)
            }
        }
        // Jours de la semaine
        Row(modifier = Modifier.fillMaxWidth()) {
            TeddDates.weekdayInitials.forEach { d ->
                Text(d, style = TeddType.Overline, color = TeddTokens.InkSubtle, textAlign = TextAlign.Center, modifier = Modifier.weight(1f))
            }
        }
        // Grille
        val cells = remember(year, month) { TeddDates.monthGrid(year, month) }
        Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
            cells.chunked(7).forEach { week ->
                if (week.all { it == null }) return@forEach
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(2.dp)) {
                    week.forEach { iso ->
                        Box(modifier = Modifier.weight(1f).aspectRatio(1f), contentAlignment = Alignment.Center) {
                            if (iso != null) {
                                val selected = iso == value
                                val isToday = iso == today
                                val ok = allowed(iso)
                                Box(
                                    modifier = Modifier
                                        .size(34.dp)
                                        .clip(CircleShape)
                                        .then(
                                            when {
                                                selected -> Modifier.background(TeddTokens.LimeStrong, CircleShape)
                                                isToday -> Modifier.border(1.5.dp, TeddTokens.LimeStrong, CircleShape)
                                                else -> Modifier
                                            },
                                        )
                                        .clickable(
                                            enabled = ok,
                                            interactionSource = remember { MutableInteractionSource() },
                                            indication = null,
                                        ) { onPick(iso) },
                                    contentAlignment = Alignment.Center,
                                ) {
                                    Text(
                                        text = iso.substring(8, 10).trimStart('0'),
                                        color = when {
                                            selected -> TeddTokens.OnAction
                                            !ok -> TeddTokens.InkSubtle
                                            isToday -> TeddTokens.LimeStrong
                                            else -> TeddTokens.Ink
                                        },
                                        fontSize = 13.sp,
                                        fontWeight = if (selected || isToday) FontWeight.SemiBold else FontWeight.Normal,
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
        Spacer(modifier = Modifier.height(2.dp))
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
            TeddButton(text = "Fermer", onClick = onDismiss, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm)
        }
    }
}

@Composable
private fun QuickDateChip(label: String, iso: String, current: String?, allowed: (String) -> Boolean, onPick: (String?) -> Unit) {
    val selected = iso == current
    val ok = allowed(iso)
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(TeddTokens.RFull))
            .background(if (selected) TeddTokens.LimeStrong else TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RFull))
            .clickable(enabled = ok, interactionSource = remember { MutableInteractionSource() }, indication = null) { onPick(iso) }
            .padding(horizontal = 10.dp, vertical = 5.dp),
    ) {
        Text(
            text = label,
            color = when { selected -> TeddTokens.OnAction; !ok -> TeddTokens.InkSubtle; else -> TeddTokens.Ink },
            fontSize = 12.sp,
            fontWeight = FontWeight.SemiBold,
        )
    }
}
